import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import axios from "axios";
import { startCase, capitalize } from "lodash";
import { FiDownload, FiLoader } from "react-icons/fi";
import ViewInfo from "components/reusable/ViewInfo";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import FormViewer from "pages/submitted_forms_by_employee/FormViewer";

const FILE_HOST = process.env.REACT_APP_AWS_MEDIA_URL;

function recordFileUrl(item) {
  if (!item) return "";

  const value = String(
    item?.fileUrl || item?.fileName || item?.file || item?.url || "",
  );

  if (!value) return "";

  if (value.includes("amazonaws.com/")) {
    const key = value.split("amazonaws.com/")[1] || "";
    return `${FILE_HOST}${key.replace(/^\/+/, "")}`;
  }

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  return `${FILE_HOST}${value.replace(/^\/+/, "")}`;
}

/* =========================================================
   TRAINING / CERTIFICATION
========================================================= */

function labelText(value, fallback) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function CredentialList({ title, rows }) {
  return (
    <div className="mt-6 px-2">
      <h3 className="text-xl font-semibold mb-3">{title}</h3>

      {rows?.length > 0 ? (
        <div className="border rounded-lg overflow-hidden bg-white">
          {rows.map((item, index) => (
            <div
              key={item?._id || index}
              className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0"
            >
              <div>
                <div className="text-sm text-slate-900 font-medium">
                  {item?.type === "training"
                    ? item?.course || "Training"
                    : item?.certificate || "Certification"}
                </div>

                <div className="text-xs text-gray-500 mt-1 capitalize">
                  {item?.jobTitle || "Current job"}
                  {" · "}
                  {item?.verification || "pending"}
                  {" · "}
                  {item?.expiryTracking || "No expiry"}
                </div>
              </div>

              {recordFileUrl(item) && (
                <a
                  href={recordFileUrl(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#2f5bff] shrink-0 hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No {title.toLowerCase()} for this job.
        </p>
      )}
    </div>
  );
}

/* =========================================================
   DOCUMENT VIEWER
========================================================= */

function EmployeeDocumentViewer({ document, onClose }) {
  if (!document) return null;

  const documentUrl = recordFileUrl(document);

  const documentTitle = document?.title
    ? document.title
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "Document";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* HEADER */}

        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">{documentTitle}</h2>

          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full border text-2xl text-gray-500 hover:text-black"
          >
            ×
          </button>
        </div>

        {/* DOCUMENT */}

        <div className="p-6 overflow-auto flex justify-center">
          {documentUrl ? (
            <iframe
              src={documentUrl}
              title={documentTitle}
              className="w-full h-[70vh] border rounded"
            />
          ) : (
            <div className="py-10 text-center">
              <p className="text-gray-500 mb-4">
                Document preview is not available.
              </p>

              {document?.fileName && (
                <p className="text-sm text-gray-700">{document.fileName}</p>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}

        {documentUrl && (
          <div className="flex justify-end px-6 py-4 border-t">
            <button
              type="button"
              onClick={() =>
                window.open(documentUrl, "_blank", "noopener,noreferrer")
              }
              className="bg-[#2f5bff] text-white px-6 py-2 rounded"
            >
              Open Document
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   EMPLOYEE VIEW
========================================================= */

const EmployeeView = ({ setEmployeeView, currentItem }) => {
  const { t } = useTranslation();

  /* =======================================================
     STATES
  ======================================================= */

  const [credentials, setCredentials] = useState([]);

  const [selectedForm, setSelectedForm] = useState(null);

  const [selectedDocument, setSelectedDocument] = useState(null);

  const [open, setOpen] = useState(false);

  const [documentOpen, setDocumentOpen] = useState(false);

  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [downloadingReportId, setDownloadingReportId] = useState("");

  /* =======================================================
     EMPLOYEE ID
  ======================================================= */

  const employeeId = currentItem?.Employee?._id || currentItem?.employeeID;

  /* =======================================================
     LEAVE DAYS
  ======================================================= */

  const noOfLeaveDays =
    currentItem?.AllLeaves?.[0]?.toDate && currentItem?.AllLeaves?.[0]?.fromDate
      ? dayjs(currentItem.AllLeaves[0].toDate).diff(
          dayjs(currentItem.AllLeaves[0].fromDate),
          "day",
        ) + 1
      : 0;

  /* =======================================================
     GET TRAINING / CERTIFICATION
  ======================================================= */

  useEffect(() => {
    if (!employeeId) {
      setCredentials([]);

      return;
    }

    apiGet(apiPath.trainingCertificationList, {
      page: 1,
      pageSize: 100,
      userId: employeeId,
      jobID: currentItem?.jobID || currentItem?._id || "",
    })
      .then((result) => {
        if (result?.data?.success) {
          setCredentials(result?.data?.results?.docs || []);
        } else {
          setCredentials([]);
        }
      })
      .catch(() => {
        setCredentials([]);
      });
  }, [employeeId, currentItem?.jobID, currentItem?._id]);

  useEffect(() => {
    if (!employeeId && !currentItem?.Employee?.email) {
      setBackgroundChecks([]);
      return;
    }
    apiGet(apiPath.backgroundCheck, {
      employeeID: employeeId || "",
      employeeEmail: currentItem?.Employee?.email || "",
      page: 1,
      limit: 20,
    })
      .then((result) => {
        const checks = result?.data?.success
          ? result.data.results?.checks || []
          : [];
        setBackgroundChecks(checks);
      })
      .catch(() => setBackgroundChecks([]));
  }, [employeeId, currentItem?.Employee?.email]);

  const employeeReports = backgroundChecks.filter((check) => {
    const checkEmployeeId = String(
      check.employeeID?._id || check.employeeID || "",
    );
    const checkEmail = String(check.employeeData?.email || "").toLowerCase();
    const employeeEmail = String(
      currentItem?.Employee?.email || "",
    ).toLowerCase();
    const samePerson =
      (employeeId && checkEmployeeId === String(employeeId)) ||
      (employeeEmail && checkEmail === employeeEmail);
    return samePerson && (check.status === "Complete" || check.reportAvailable);
  });

  const reportUrl = (check) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL || "";
    return `${baseURL}${apiPath.backgroundCheck}/${check._id}/report-pdf`;
  };

  const downloadReport = async (check) => {
    if (!check?._id || downloadingReportId) return;
    setDownloadingReportId(check._id);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(reportUrl(check), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `background_check_report_${check.credibledUUID || check._id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      window.alert("Report is not available yet.");
    } finally {
      setDownloadingReportId("");
    }
  };

  /* =======================================================
     TRAINING / CERTIFICATION
  ======================================================= */

  const trainingRows = credentials.filter((item) => item?.type === "training");

  const certificationRows = credentials.filter(
    (item) => item?.type === "certification",
  );

  /* =======================================================
     EMPLOYEE FORMS
  ======================================================= */

  const employeeForms = (
    Array.isArray(currentItem?.EmployeeFilledForms)
      ? currentItem.EmployeeFilledForms.flat(3)
      : []
  ).filter(
    (form) =>
      form &&
      typeof form === "object" &&
      (form._id || form.formId || form.formType),
  );

  /* =======================================================
     EMPLOYEE DOCUMENTS
  ======================================================= */

  const employeeDocuments = currentItem?.EmployeeDocuments || [];

  /* =======================================================
     HANDLE FORM VIEW
  ======================================================= */

  const handleView = (id) => {
    const form = employeeForms.find((item) => item?._id === id);

    if (!form?.formData) {
      return;
    }

    setSelectedForm({
      structure: form.formData,
      data: form,
    });
    setOpen(true);
  };

  /* =======================================================
     HANDLE DOCUMENT VIEW
  ======================================================= */

  const handleDocumentView = (doc) => {
    setSelectedDocument(doc);

    setDocumentOpen(true);
  };

  /* =======================================================
     CLOSE FORM
  ======================================================= */

  const handleCloseForm = () => {
    setOpen(false);

    setSelectedForm(null);
  };

  /* =======================================================
     CLOSE DOCUMENT
  ======================================================= */

  const handleCloseDocument = () => {
    setDocumentOpen(false);

    setSelectedDocument(null);
  };

  /* =======================================================
     FORMS FOR CURRENT EMPLOYEE / JOB
  ======================================================= */

  const filteredForms = employeeForms.filter((form) => {
    /*
     * If jobID exists, use it.
     *
     * If jobID is missing, still show the form.
     * This prevents Employee Agreement from
     * disappearing because of incomplete data.
     */

    if (!form?.jobID) {
      return true;
    }

    return String(form.jobID) === String(currentItem?._id);
  });

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {/* =====================================================
          EMPLOYEE MODAL
      ===================================================== */}

      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form
            className="sm:py-4 sm:px-2 py-8 px-7"
            onSubmit={(event) => event.preventDefault()}
          >
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full min-w-[940px] max-h-[96vh] overflow-y-auto bg-white outline-none focus:outline-none">
              {/* =================================================
                  HEADER
              ================================================= */}

              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">{t("O_VIEW_USER")}</h3>

                <button
                  type="button"
                  className="ml-auto flex items-center justify-center text-3xl text-black border-2 rounded-full h-8 w-8 leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => setEmployeeView(false)}
                >
                  <span className="text-[#B8BBBF] text-4xl" title="Close">
                    ×
                  </span>
                </button>
              </div>

              {/* =================================================
                  EMPLOYEE DETAILS
              ================================================= */}

              <div className="relative p-6 flex-auto">
                <h3 className="text-xl font-semibold">Employee details</h3>

                <div className="grid grid-cols-3">
                  {/* <ViewInfo
                    label="Employee ID"
                    value={currentItem?.Employee?.employeeId || ""}
                  /> */}
                  <ViewInfo label="Employee ID" value={currentItem?.employmentStatus === "terminated" ? `${currentItem?.Company.name.substring(0, 3)}${currentItem.Employee._id.toString().slice(-3)}` :currentItem?.Employee?.employeeId} />
                  <ViewInfo label="Employee name" value={currentItem?.Employee?.name || ""} />
                  <ViewInfo
                    label="Employee name"
                    value={currentItem?.Employee?.name || ""}
                  />

                  <ViewInfo
                    label="Email ID"
                    value={currentItem?.Employee?.email || ""}
                  />

                  <ViewInfo
                    label="Employment status"
                    value={capitalize(currentItem?.employmentStatus) || ""}
                  />

                  <ViewInfo
                    label="Year of experience"
                    value={currentItem?.yearOfExperiece || ""}
                  />

                  {currentItem?.Employee?.dob && (
                    <ViewInfo
                      label="DOB"
                      value={dayjs(currentItem.Employee.dob).format(
                        "MM-DD-YYYY",
                      )}
                    />
                  )}

                  <ViewInfo
                    label="Country"
                    value={currentItem?.EmployeeCountry?.name || ""}
                  />

                  <ViewInfo
                    label="State"
                    value={currentItem?.EmployeeState?.name || ""}
                  />

                  <ViewInfo
                    label="City"
                    value={currentItem?.Employee?.city || ""}
                  />

                  <ViewInfo
                    label="Skills"
                    value={
                      currentItem?.Skills?.map((item) => item?.name).join(
                        ", ",
                      ) || ""
                    }
                  />

                  <ViewInfo
                    label="Job title"
                    value={currentItem?.Job?.name || ""}
                  />

                  <ViewInfo
                    label="Department"
                    value={currentItem?.Department?.name || ""}
                  />

                  <ViewInfo
                    label="Employment length [in days]"
                    value={currentItem?.Job?.employmentLength || ""}
                  />

                  {currentItem?.AllLeaves?.[0]?.fromDate && (
                    <ViewInfo
                      label="Leave"
                      value={`
                        ${dayjs(currentItem.AllLeaves[0].fromDate).format(
                          "MM-DD-YYYY",
                        )}
                        to
                        ${dayjs(currentItem.AllLeaves[0].toDate).format(
                          "MM-DD-YYYY",
                        )}
                        (${noOfLeaveDays}
                        ${noOfLeaveDays > 1 ? "days" : "day"})
                      `}
                    />
                  )}

                  <ViewInfo
                    label="Available"
                    value={currentItem?.isOnLeave ? "No" : "Yes"}
                  />

                  <ViewInfo
                    label="Form Status"
                    value={startCase(currentItem?.formStatus) || "N/A"}
                  />

                  <ViewInfo
                    label="Duration Type"
                    value={currentItem?.duration || ""}
                  />

                  <ViewInfo
                    label="Hire date"
                    value={
                      currentItem?.acceptedRejectedAt
                        ? dayjs(currentItem.acceptedRejectedAt).format(
                            "MM-DD-YYYY",
                          )
                        : ""
                    }
                  />

                  {/* LINKEDIN */}

                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>LinkedIn</strong>

                      <h6>
                        {currentItem?.Employee?.linkedInLink ? (
                          <a
                            href={currentItem.Employee.linkedInLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Profile
                          </a>
                        ) : (
                          <span> LinkedIn profile not added </span>
                        )}
                      </h6>
                    </div>
                  </div>

                  {/* FACE ID */}

                  <div className="sm:py-4 sm:px-2 py-8 px-7">
                    <div className="relative z-0 mb-2 w-full group">
                      <strong>Face ID</strong>

                      <h6>
                        {currentItem?.Employee?.faceImage ? (
                          <a
                            href={`https://d2n5q7sk1gggpw.cloudfront.net/${currentItem.Employee.faceImage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Image
                          </a>
                        ) : (
                          <span> Face ID not added </span>
                        )}
                      </h6>
                    </div>
                  </div>
                </div>

                {/* TRAINING */}

                <CredentialList title="Training" rows={trainingRows} />

                {/* CERTIFICATION */}

                <CredentialList
                  title="Certification"
                  rows={certificationRows}
                />

                <div className="mt-8 px-2">
                  <h3 className="text-xl font-semibold">
                    Forms submitted by the employee (Submitted date:{" "}
                    {currentItem?.createdAt
                      ? dayjs(currentItem.createdAt).format("MM-DD-YYYY")
                      : "N/A"}
                    )
                  </h3>
                  <div className="sm:py-4 sm:px-2">
                    {filteredForms.length > 0 ? (
                      <div className="relative z-0 mb-2 w-full group">
                        <strong className="block mb-2">Form title</strong>
                        <div className="flex flex-wrap">
                          {filteredForms.map((form, index) => (
                            <button
                              key={form?._id || form?.formId || index}
                              type="button"
                              onClick={() => handleView(form?._id)}
                              className="mb-3 block text-base underline mr-3 hover:text-LightBlue text-left"
                            >
                              {labelText(
                                form?.formType ||
                                  form?.formData?.formType ||
                                  form?.formData?.formName,
                                "Employee Agreement",
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mb-3">
                        No forms submitted by this employee.
                      </p>
                    )}
                    {employeeDocuments.length > 0 && (
                      <div className="relative z-0 mb-2 w-full group">
                        <strong className="block mb-2">Other Documents</strong>
                        <div className="flex flex-wrap gap-4">
                          {employeeDocuments.map((doc, index) => (
                            <div
                              key={doc?._id || index}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDocumentView(doc)}
                              className="bg-gray-50 p-3 rounded-md min-w-[200px]"
                            >
                              <div className="mb-2">
                                <p className="font-medium text-gray-900">
                                  {labelText(doc?.title, "Document")}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {/* {doc?.expiryDate ? dayjs(doc.expiryDate).format("MM-DD-YYYY") : "No expiry"} */}
                                  view
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 px-2">
                  <h3 className="text-xl font-semibold mb-3">
                    Background verification
                  </h3>
                  {employeeReports.length ? (
                    <div className="space-y-3">
                      {employeeReports.map((check) => (
                        <div
                          key={check._id}
                          className="flex items-center justify-between gap-3 px-4 py-3 border rounded-lg bg-white"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-900 font-medium">
                              Background Check Report
                            </span>
                            {check.status && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 capitalize">
                                {check.status}
                              </span>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => downloadReport(check)}
                            disabled={downloadingReportId === check._id}
                            className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-[#2f5bff] text-white hover:bg-[#1e46d6] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                          >
                            {downloadingReportId === check._id ? (
                              <>
                                <FiLoader className="animate-spin" size={16} />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <FiDownload size={16} />
                                Download Report
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No background verification report yet.
                    </p>
                  )}
                </div>
              </div>
              {/* FOOTER */}

              <div className="border-t border-solid border-slate-200 rounded-b flex items-center justify-center p-6">
                <button
                  onClick={() => setEmployeeView(false)}
                  className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
                  type="button"
                >
                  {t("O_BACK")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* BACKGROUND OVERLAY */}

      <div className="opacity-25 fixed inset-0 z-40 bg-black" />

      {open && selectedForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-[960px] max-h-[90vh]">
            <FormViewer
              formStructure={selectedForm.structure}
              formData={selectedForm.data}
              onClose={handleCloseForm}
            />
          </div>
        </div>
      )}

      {documentOpen && selectedDocument && (
        <EmployeeDocumentViewer
          document={selectedDocument}
          onClose={handleCloseDocument}
        />
      )}
    </>
  );
};

export default EmployeeView;
